import React, { useCallback } from 'react';

import Button from '@atlaskit/button/new';
import Image from '@atlaskit/image';
import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	type OnCloseHandler,
} from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives/compiled';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { WhiteboardInternalModalProps } from './WhiteboardInternalModal';

export type WhiteboardInternalDialogProps = {
	modalProps: WhiteboardInternalModalProps;
	closeModal: () => void;
};

export const WhiteboardInternalDialog: React.FC<WhiteboardInternalDialogProps> = ({
	modalProps,
	closeModal,
}: WhiteboardInternalDialogProps) => {
	const {
		props: dialogProps,
		modalHeader: {
			title: { content: titleContent, props: titleProps },
			props: headerProps,
		},
		modalBody: { content: bodyContent, props: bodyProps },
		modalFooter: { buttons, props: footerProps },
		image: svgImage,
	} = modalProps;

	const onClose: OnCloseHandler = useCallback(
		(e, analyticsEvent) => {
			dialogProps?.onClose?.(e, analyticsEvent);
			closeModal();
		},
		[closeModal, dialogProps],
	);

	return (
		<ModalDialog {...dialogProps} onClose={onClose}>
			{svgImage && <Image src={svgImage} alt="Dialog image" />}
			<ModalHeader {...headerProps} hasCloseButton>
				<ModalTitle {...titleProps}>{titleContent}</ModalTitle>
			</ModalHeader>
			<ModalBody {...bodyProps}>
				<Box as="p" dangerouslySetInnerHTML={{ __html: bodyContent }} />
			</ModalBody>
			<ModalFooter {...footerProps}>
				{buttons.map((b, i) => {
					const { shouldCloseModal, props } = b;

					const onClick = (
						e: React.MouseEvent<HTMLButtonElement>,
						analyticsEvent: UIAnalyticsEvent,
					) => {
						props?.onClick?.(e, analyticsEvent);

						if (shouldCloseModal) {
							onClose(e, analyticsEvent);
						}
					};

					return (
						<Button {...b.props} key={`whiteboard-modal-button-${i}`} onClick={onClick}>
							{b.label}
						</Button>
					);
				})}
			</ModalFooter>
		</ModalDialog>
	);
};
